import { z } from 'zod';

import { fieldBindingSchema } from '../schemas/templateSchemas';

/**
 * What a Document Template step stores in `WorkflowStep.Settings`.
 *
 * ONE template per step. A workflow that generates several documents adds several
 * Document Template steps, and the workflow's own step order is the generation
 * order — rather than a second ordering hidden inside one step's settings, which
 * would be invisible from the builder's step list.
 *
 * This is the only thing Studio's own database keeps about a template: a pointer to
 * it, and where each field's value comes from at run time. The document, field
 * definitions, signers and signature coordinates all live in SignCare — see
 * .claude/docs/template-feature-plan.md §5.
 */
export const templateStepSettingsSchema = z.object({
  templateId: z.string().nullable().default(null),
  /** Cached for display before the library loads; the library is authoritative. */
  templateName: z.string().nullable().default(null),
  fieldBindings: z.array(fieldBindingSchema.extend({ fieldName: z.string() })).default([]),
});

export type TemplateStepSettings = z.infer<typeof templateStepSettingsSchema>;

const EMPTY: TemplateStepSettings = { templateId: null, templateName: null, fieldBindings: [] };

/**
 * Reads the template settings out of a step's raw settings JSON.
 *
 * Tolerant by design: the same blob carries the generic step settings every other
 * step type writes, and a step may predate this feature. Anything unparseable reads
 * as "no template attached".
 */
export function parseTemplateStepSettings(raw: string | null): TemplateStepSettings {
  if (!raw) return EMPTY;

  try {
    const parsed = templateStepSettingsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : EMPTY;
  } catch {
    return EMPTY;
  }
}

/**
 * Merges template settings back into a step's settings JSON, preserving every key
 * this feature does not own — the blob is shared with the generic settings tab and
 * the advanced-rules grouping, and clobbering it would silently reset both.
 */
export function writeTemplateStepSettings(
  raw: string | null,
  next: Partial<TemplateStepSettings>,
): string {
  let base: Record<string, unknown> = {};
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') base = parsed as Record<string, unknown>;
    } catch {
      /* unparseable — start clean rather than lose the new template link */
    }
  }

  return JSON.stringify({ ...base, ...next });
}
